import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Upload from 'hooks/useDirectUpload/Upload';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import {
  collectedFacilities,
  FacilityOption,
  pickedFacilityIds,
  PORTAL_COLLECT_FIELDS,
  ReferralFieldDef,
  sourceSystemLabel,
  splitCollectedPatientName,
  VALUE_NAMES,
} from './constants';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  // Values read off the portal elsewhere (the extension's manual HUD), landing in the fields
  // for the person to review — never overwriting what they have already entered here.
  prefill?: Record<string, unknown> | null;
}

const TEXT_COLLECT_FIELDS = PORTAL_COLLECT_FIELDS.filter((field) => field.type !== 'date');

// Where to go get the information — the whole instruction the kickoff carried. A URL
// pointer renders as a link (and later becomes the browser-extension hand-off).
function SourceContext({ workflow }: { workflow: Workflow }) {
  const system = workflow.valueFor('source_system')?.value;
  const pointer = workflow.valueFor('retrieval_pointer')?.value;
  if (!system && !pointer) return null;

  return (
    <SourceLine data-testid='retrieve-source-context'>
      {system && <BodySmallBold>{sourceSystemLabel(system)}</BodySmallBold>}
      {pointer &&
        (/^https?:\/\//.test(pointer) ? (
          <PointerLink href={pointer} target='_blank' rel='noreferrer'>
            {pointer}
          </PointerLink>
        ) : (
          <Pointer>{pointer}</Pointer>
        ))}
    </SourceLine>
  );
}

// Document-first manual fallback for the source-portal pull: the person saves the
// referral documents out of the portal and uploads them here — the AI actions read the
// demographics from the documents. The collect-fields are the optional shortcut: any
// value typed here is committed at completion and outranks the downstream extraction,
// so what a person read off the portal is never overwritten by the AI.
export default function RetrievePortalForm({ action, workflow, onComplete, isCompleting, prefill }: Props) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_COLLECT_FIELDS.map((field) => [field.name, '']))
  );
  const [dob, setDob] = useState<Date | null>(null);
  // The facilities this referral names — one row each, every one a pick, so what this form
  // collects is always an Olio facility and never something the mapping step has to resolve.
  const [facilities, setFacilities] = useState<(FacilityOption | null)[]>([null]);

  // The same facility source the referral modal picks from.
  const { facilities: allowedFacilities } = useAllowedFacilities();
  const facilityOptions: FacilityOption[] = useMemo(
    () => allowedFacilities.map((group) => ({ id: group.id, name: group.name })),
    [allowedFacilities]
  );

  // A facility already picked in another row is off the menu — picking it twice would address
  // the same building twice and collapse to one entry anyway.
  const optionsFor = (row: FacilityOption | null) =>
    facilityOptions.filter((option) => option.id === row?.id || !facilities.some((pick) => pick?.id === option.id));

  // Prefill fills only fields the person hasn't touched — their own typing wins.
  // The ref guard ensures this runs at most once — facilityOptions may gain a new identity
  // when the groups query resolves, and re-running would clobber anything the person typed.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!prefill || prefilled.current) return;
    // Facility lookup needs the options list; wait for it to load before applying anything.
    if (facilityOptions.length === 0) return;
    prefilled.current = true;
    const stringAt = (name: string) => {
      const value = prefill[name];
      return typeof value === 'string' ? value : '';
    };
    setText((prev) => Object.fromEntries(Object.entries(prev).map(([name, value]) => [name, value || stringAt(name)])));
    if (stringAt(VALUE_NAMES.PATIENT_DOB)) {
      setDob((prev) => prev ?? parseDate(stringAt(VALUE_NAMES.PATIENT_DOB)) ?? null);
    }
    // The HUD picks from the same facility list, so its entries arrive as ids to look up.
    const prefillFacilities = pickedFacilityIds(prefill[VALUE_NAMES.FACILITIES])
      .map((id) => facilityOptions.find((option) => option.id === id))
      .filter((option): option is FacilityOption => !!option);
    if (prefillFacilities.length > 0) setFacilities((prev) => (prev.some(Boolean) ? prev : prefillFacilities));
  }, [prefill, facilityOptions]);

  const readyUploads = uploads.filter((upload) => upload.signedId);
  const ready = readyUploads.length > 0 && !isUploading && !isCompleting;

  const typedValues = (): WorkflowValuesInput => {
    const values: WorkflowValuesInput = {};
    TEXT_COLLECT_FIELDS.forEach((field) => {
      const value = text[field.name].trim();
      if (value) values[field.name] = value;
    });
    const dashDob = simpleDashDate(dob);
    if (dashDob) values[VALUE_NAMES.PATIENT_DOB] = dashDob;
    return values;
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    onComplete(action.id, {
      attachments: readyUploads.map((upload) => ({ signedId: upload.signedId!, filename: upload.file.name })),
      [VALUE_NAMES.FACILITIES]: collectedFacilities(facilities),
      ...splitCollectedPatientName(typedValues()),
    });
  }

  const setFacilityRow = (index: number, row: FacilityOption | null) =>
    setFacilities((prev) => prev.map((current, i) => (i === index ? row : current)));

  const removeFacility = (index: number) =>
    setFacilities((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [null]));

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        <Optional> (optional)</Optional>
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          <Select
            inputId={`retrieve-field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`retrieve-field-${field.name}`}
        />
      )}
    </Field>
  );

  return (
    <Form onSubmit={submit} data-testid='retrieve-portal-form'>
      <SourceContext workflow={workflow} />
      <BodySmall>
        Save the referral documents from the source portal (download or print to PDF), then upload them here. The
        patient&apos;s details are read from the documents.
      </BodySmall>
      <UploadZone data-testid='retrieve-documents'>
        <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
      </UploadZone>
      <Field data-testid='retrieve-facilities'>
        <FieldLabel>Facilities</FieldLabel>
        {facilities.map((row, index) => (
          // Index keys are safe here: rows are positional (no identity beyond their slot).
          <FacilityRowLine key={index}>
            <SelectCell data-testid={`retrieve-facility-${index}`}>
              <Select<FacilityOption>
                inputId={`retrieve-facility-${index}`}
                placeholder='Select a facility'
                options={optionsFor(row)}
                value={row}
                onChange={(pick) => setFacilityRow(index, pick)}
                menuPortalTarget={document.body}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
              />
            </SelectCell>
            <RowButton
              type='button'
              aria-label='Remove facility'
              onClick={() => removeFacility(index)}
              data-testid={`retrieve-facility-remove-${index}`}>
              ×
            </RowButton>
          </FacilityRowLine>
        ))}
        <AddLink
          type='button'
          onClick={() => setFacilities((prev) => [...prev, null])}
          data-testid='retrieve-facility-add'>
          + Add facility
        </AddLink>
      </Field>
      <BodySmall>Anything you already know from the portal can be entered directly:</BodySmall>
      <FieldGrid data-testid='retrieve-collect-fields'>{PORTAL_COLLECT_FIELDS.map(renderField)}</FieldGrid>
      <div>
        <Button type='submit' size='small' disabled={!ready} data-testid='retrieve-submit'>
          Save retrieved documents
        </Button>
      </div>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadZone = styled.div`
  margin-top: 4px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Optional = styled.span`
  color: ${colors.black50};
  font-weight: 400;
`;

const FacilityRowLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
`;

const SelectCell = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${colors.black50};
  font-size: 16px;
  line-height: 1;
`;

const AddLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;
`;

const SourceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.black5};
`;

const Pointer = styled(BodySmall)`
  color: ${colors.black50};
  word-break: break-all;
`;

const PointerLink = styled.a`
  font-size: 12px;
  color: ${colors.blue100};
  word-break: break-all;
`;
